import React from "react";
import {
  IconHash,
  IconCurrencyDollar,
  IconLink,
  IconTag,
  IconBox,
  IconScale,
  IconCalculator,
  IconReceipt,
} from "@tabler/icons-react";
import { useMemo } from "react";
import { Badge } from "visuais";
import {
  usePrenotaStore,
  produtosColorSchemes,
  type InfoCardData,
  ConexaoService,
  type ProdutoPedido,
  type ConexaoStats,
} from "@PN/inclusao";

// =================================================================
// TIPOS E INTERFACES
// =================================================================

// Tipos importados do ConexaoService

export interface ProdutoTableColumn {
  key: keyof ProdutoPedido;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  width: string;
  align?: "left" | "right" | "center";
  render?: (value: any, produto: ProdutoPedido) => React.ReactNode;
}

// =================================================================
// CONFIGURAÇÃO DA TABELA
// =================================================================

const columns: ProdutoTableColumn[] = [
  {
    key: "codProduto",
    label: "Código",
    icon: IconTag,
    width: "w-[100px] min-w-[100px]",
    render: (value) => (
      <span className="text-[7.5pt] font-medium text-cyan-600 dark:text-cyan-400">
        {value}
      </span>
    ),
  },
  {
    key: "descricao",
    label: "Descrição",
    icon: IconBox,
    width: "max-w-[100px]",
    render: (value, produto) => (
      <div className="flex flex-col gap-0">
        <span className="text-[7.5pt] font-medium text-cyan-600 dark:text-cyan-400 truncate whitespace-nowrap">
          {value}
        </span>
        {produto.unidadeMedida && (
          <span className="text-[7pt] text-cyan-500 dark:text-cyan-500">
            UM: {produto.unidadeMedida}
          </span>
        )}
        {produto.observacao && (
          <span className="text-[7pt] text-cyan-600 dark:text-cyan-400 italic truncate">
            Obs: {produto.observacao}
          </span>
        )}
      </div>
    ),
  },
  {
    key: "quantidade",
    label: "Qtd",
    icon: IconScale,
    width: "w-[80px] min-w-[80px]",
    align: "right",
    render: (value) => (
      <span className="text-[7.5pt] font-semibold text-cyan-600 dark:text-cyan-400">
        {value.toLocaleString("pt-BR")}
      </span>
    ),
  },

  {
    key: "valorTotal",
    label: "Total",
    icon: IconReceipt,
    width: "w-[100px] min-w-[100px]",
    align: "right",
    render: (value) => (
      <span className="text-[7.5pt] font-bold text-cyan-600 dark:text-cyan-400">
        R$ {value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
      </span>
    ),
  },
];

// =================================================================
// SERVIÇO DE CONEXÃO (SINGLE RESPONSIBILITY)
// =================================================================

// Serviço importado do ConexaoService.ts

// =================================================================
// FUNÇÕES DE CÁLCULO
// =================================================================

/**
 * Hook para calcular estatísticas de conexão usando o ConexaoService
 * Segue DRY: Reutiliza lógica do Connector via ConexaoService
 * Segue SRP: Responsabilidade única de calcular conexões
 */
export const useConexaoStats = (produtos: ProdutoPedido[]): ConexaoStats => {
  const { aux, draft } = usePrenotaStore();

  return useMemo(() => {
    return ConexaoService.calculateStats(produtos, aux, draft);
  }, [produtos, aux, draft]);
};

/**
 * Função para calcular totais dos produtos
 * OTIMIZADO: Usa um único reduce em vez de dois
 */
export const calcularTotais = (produtos: ProdutoPedido[]) => {
  return produtos.reduce(
    (acc, produto) => ({
      totalGeral: acc.totalGeral + produto.valorTotal,
      quantidadeTotal: acc.quantidadeTotal + produto.quantidade,
    }),
    { totalGeral: 0, quantidadeTotal: 0 }
  );
};

/**
 * Função para gerar chave única para produtos
 */
export const gerarChaveUnica = (
  produto: ProdutoPedido,
  index: number,
  sufixo?: string
) => {
  const baseKey = produto.idUnico || produto.codProduto;
  const sufixoStr = sufixo ? `_${sufixo}` : "";
  return `${baseKey}${sufixoStr}_${index}`;
};

// =================================================================
// CONFIGURAÇÃO DOS CARDS DE RESUMO
// =================================================================

export const getResumoCards = (
  quantidadeTotal: number,
  totalGeral: number,
  conexaoStats: ConexaoStats
): InfoCardData[] => [
  {
    icon: IconHash,
    label: "Quantidade Total",
    value: quantidadeTotal.toLocaleString("pt-BR"),
    colorScheme: produtosColorSchemes[0],
  },
  {
    icon: IconCurrencyDollar,
    label: "Valor Total",
    value: `R$ ${totalGeral.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
    })}`,
    colorScheme: produtosColorSchemes[1],
  },
  {
    icon: IconLink,
    label: "Conexão Possível",
    value: `${conexaoStats.conectados}/${
      conexaoStats.totalPedido
    } (${conexaoStats.percentualPedido.toFixed(0)}%)`,
    colorScheme: produtosColorSchemes[2],
  },
];

// =================================================================
// HOOK PRINCIPAL
// =================================================================

export const useProdutosLogic = (produtos: ProdutoPedido[]) => {
  // OTIMIZAÇÃO: Memoizar cálculos básicos
  const { totalGeral, quantidadeTotal } = useMemo(
    () => calcularTotais(produtos),
    [produtos]
  );

  const conexaoStats = useConexaoStats(produtos);

  // OTIMIZAÇÃO: Memoizar resumo cards
  const resumoCards = useMemo(
    () => getResumoCards(quantidadeTotal, totalGeral, conexaoStats),
    [quantidadeTotal, totalGeral, conexaoStats]
  );

  // OTIMIZAÇÃO: Memoizar filtros
  const produtosComObservacao = useMemo(
    () => produtos.filter((p) => p.observacao),
    [produtos]
  );

  const produtosSemConexao = useMemo(() => {
    return ConexaoService.filterUnconnectedProducts(
      produtos,
      conexaoStats.conexoesPossiveis
    );
  }, [produtos, conexaoStats.conexoesPossiveis]);

  return {
    // Dados calculados
    totalGeral,
    quantidadeTotal,
    conexaoStats,
    resumoCards,

    // Dados filtrados
    produtosComObservacao,
    produtosSemConexao,

    // Configurações
    columns,

    // Funções utilitárias
    gerarChaveUnica,
  };
};
